import React, { useEffect, useMemo, useState } from 'react'

import dayjs from 'dayjs'
import useBatchOperation from '@hooks/shared/useBatchOperation'
import useCan from '@shared/permissions/useCan'
import { MultipleValue } from '@shared/table/Rules/component'
import FullDayRangePicker from '@components/shared/FullDayRangePicker'
import TableIndex from '@shared/table/TableIndex'
import type { TableService, SupportRule, TableAction } from '@shared/table/TableIndex'
import { Tag } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import type { Permission } from '@/permissions_manifest.generated'

const STATUS_COLORS: Record<string, string> = {
  draft: 'default',
  submitted: 'orange',
  approved: 'green',
  partial_received: 'blue',
  received: 'cyan',
  closed: 'default',
  canceled: 'red',
}

const ALL_STATUSES = ['draft', 'submitted', 'approved', 'partial_received', 'received', 'closed', 'canceled']

interface MerchantOption {
  id: string;
  name: string;
}

interface SupplierService {
  getRecords: (params?: Record<string, unknown>) => Promise<{ success?: boolean; data?: Array<{ id: string; name: string }> }>;
}

interface PurchaseOrderListViewProps {
  service: TableService;
  role?: 'admin' | 'operator';
  showMerchantColumn?: boolean;
  merchants?: MerchantOption[];
  supplierService: SupplierService;
  addJob?: (...args: unknown[]) => void;
}

interface PurchaseOrderRow {
  id: string;
  name?: string;
  status?: string;
  merchant_name?: string;
  supplier_name?: string;
  currency?: string;
  total_amount?: number | string | null;
  last_inbound_id?: string | null;
  last_inbound_name?: string | null;
  last_inbound_received_at?: string | null;
  expected_delivery_date?: string;
  created_at?: string;
  [key: string]: unknown;
}

export default function PurchaseOrderListView({
  service,
  role = 'admin',
  showMerchantColumn = false,
  merchants = [],
  supplierService,
  addJob,
}: PurchaseOrderListViewProps) {
  const { t, i18n } = useTranslation()
  const can = useCan()

  const [suppliers, setSuppliers] = useState<Array<{ id: string; name: string }>>([])
  useEffect(() => {
    let cancelled = false
    supplierService.getRecords({ page: 1, page_size: 100 })
      .then((res) => {
        if (cancelled) return
        if (res?.success && res.data) {
          setSuppliers(res.data)
          return
        }
        // 拿到 response 但非成功（5xx / 422 / 非預期 body）— adminApiFetch 不會 throw，
        // 這裡若不 log 會變成「下拉靜默變空」，工程師無從得知 API 失敗。
        console.error('Failed to load suppliers for purchase order list filter (unexpected response):', res)
      })
      .catch((error: unknown) => {
        if (cancelled) return
        console.error('Failed to load suppliers for purchase order list filter:', error)
      })
    return () => { cancelled = true }
  }, [supplierService])
  // service 為 TableService 型別，但實際 runtime 物件帶有 export 方法（PO service）；
  // useBatchOperation 第一參數僅作透傳，故以 unknown 邊界收斂。
  const executeBatchOperation = useBatchOperation(service as unknown, addJob as ((jobId: string, jobName: string, ...args: unknown[]) => void) | undefined)

  const columns = useMemo(() => {
    const cols: unknown[] = [
      {
        nameKey: 'models.purchase_order.name',
        persist: true,
        sortable: true,
        sortFieldName: 'purchase_orders.name',
        width: '180px',
        wrap: true,
        cell: ({ id, name }: PurchaseOrderRow) => (
          <Link to={`/purchase_orders/${id}`} style={{ wordBreak: 'break-word' }}>
            {name}
          </Link>
        ),
      },
      {
        nameKey: 'models.purchase_order.status',
        width: '120px',
        cell: ({ status }: PurchaseOrderRow) => (
          <Tag color={STATUS_COLORS[status as string] || 'default'}>
            {t(`enums.purchase_order.status.${status}`)}
          </Tag>
        ),
      },
    ]

    if (showMerchantColumn) {
      cols.push({
        nameKey: 'models.purchase_order.merchant',
        width: '150px',
        wrap: true,
        selector: ({ merchant_name }: PurchaseOrderRow) => merchant_name || '-',
      })
    }

    cols.push(
      {
        nameKey: 'models.purchase_order.supplier',
        width: '150px',
        wrap: true,
        selector: ({ supplier_name }: PurchaseOrderRow) => supplier_name || '-',
      },
      {
        nameKey: 'models.purchase_order.total_amount',
        width: '120px',
        selector: ({ currency, total_amount }: PurchaseOrderRow) => {
          if (total_amount == null) return '-'
          return `${currency || ''} ${Number(total_amount).toLocaleString()}`
        },
      },
      {
        nameKey: 'models.purchase_order.last_inbound_name',
        width: '150px',
        wrap: true,
        cell: ({ last_inbound_id, last_inbound_name }: PurchaseOrderRow) =>
          last_inbound_id ? (
            <Link to={`/inbounds/${last_inbound_id}`} style={{ wordBreak: 'break-word' }}>
              {last_inbound_name}
            </Link>
          ) : '-',
      },
      {
        nameKey: 'models.purchase_order.last_inbound_received_at',
        width: '150px',
        wrap: true,
        selector: ({ last_inbound_received_at }: PurchaseOrderRow) =>
          last_inbound_received_at ? dayjs(last_inbound_received_at).format('YYYY-MM-DD HH:mm') : '-',
      },
      {
        nameKey: 'models.purchase_order.expected_delivery_date',
        width: '130px',
        wrap: true,
        selector: ({ expected_delivery_date }: PurchaseOrderRow) =>
          expected_delivery_date ? dayjs(expected_delivery_date).format('YYYY-MM-DD') : '-',
      },
      {
        nameKey: 'common.created_at',
        sortable: true,
        sortFieldName: 'purchase_orders.created_at',
        width: '150px',
        wrap: true,
        selector: ({ created_at }: PurchaseOrderRow) => created_at,
      },
    )

    return cols
  }, [t, showMerchantColumn])

  const supportRules = useMemo(() => {
    const rules: SupportRule[] = [
      {
        label: t('models.purchase_order.status'),
        component: MultipleValue,
        inputName: 'status',
        createable: false,
        defaultPinned: true,
        defaultOptions: ALL_STATUSES.map(s => ({
          label: t(`enums.purchase_order.status.${s}`),
          value: s,
        })),
      },
    ]

    if (showMerchantColumn && merchants.length > 0) {
      rules.push({
        label: t('models.purchase_order.merchant'),
        component: MultipleValue,
        inputName: 'merchant_id',
        createable: false,
        defaultOptions: merchants.map(m => ({
          label: m.name,
          value: m.id,
        })),
      })
    }

    rules.push(
      {
        label: t('models.purchase_order.supplier'),
        component: MultipleValue,
        inputName: 'supplier_id',
        createable: false,
        defaultOptions: suppliers.map(s => ({ label: s.name, value: s.id })),
      },
      {
        label: t('common.created_at'),
        component: FullDayRangePicker,
        inputName: 'purchase_orders.created_at',
      },
      {
        label: t('models.purchase_order.expected_delivery_date'),
        component: FullDayRangePicker,
        inputName: 'purchase_orders.expected_delivery_date',
      },
      {
        label: t('models.purchase_order.last_inbound_received_at'),
        component: FullDayRangePicker,
        inputName: 'inbound_received_at',
      },
      {
        label: t('models.purchase_order.last_inbound_name'),
        component: MultipleValue,
        inputName: 'inbound_name',
        createable: true,
      },
    )

    return rules
  }, [t, showMerchantColumn, merchants, suppliers])

  const actions = useMemo(() => {
    const all: Array<{ name: string; className: string; requiredPermission?: Permission; do: (selected: Iterable<unknown>) => void }> = [
      {
        name: 'common.button.export',
        className: 'btn btn-primary btn-outline mb-0',
        requiredPermission: 'purchase_orders:export',
        do: (selected) => executeBatchOperation({
          task: () => (service as unknown as { export: (ids: unknown[], lang: string) => Promise<unknown> })
            .export(Array.from(selected), i18n.language),
          jobName: 'common.button.export',
        }),
      },
    ]
    // requiredPermission 為本元件權限過濾用的額外欄位，非 TableAction 標準欄位；
    // 過濾後僅保留 TableAction 相容欄位，故於邊界 cast。
    return all.filter(item => !item.requiredPermission || can(item.requiredPermission)) as TableAction[]
  }, [executeBatchOperation, service, i18n.language, can])

  // defaultSortField / defaultSortDirection 不在 TableIndexProps 上，但原 .jsx 有傳，
  // 為保留 runtime 行為（轉換 ≠ 重構）以未型別化 spread 通過。
  const extraTableProps = {
    defaultSortField: 'purchase_orders.created_at',
    defaultSortDirection: 'desc',
  } as Record<string, unknown>;

  return (
    <TableIndex
      service={service}
      tableId={`${role}PurchaseOrdersTable`}
      columns={columns}
      supportRules={supportRules}
      actions={actions}
      searchable={{
        match: 'contains',
        searchFields: [
          t('models.purchase_order.name'),
          t('models.purchase_order.supplier'),
        ],
      }}
      columnsEditable={true}
      {...extraTableProps}
    />
  )
}
